import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useGeolocation } from 'src/hooks/geo'
import { Asset } from 'src/types'

// We can't import Leaflet as usual because it breaks on server-side.
// Also, we can't conditionally import, so we have to fall back to require
declare const window: any
declare const __CLIENT__: boolean
const L = __CLIENT__ ? require('leaflet') : null
__CLIENT__ && require('leaflet.markercluster')

const tileUrl = process.env.TILE_URL || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'

export const useMap = (id: string) => {
  const history = useHistory<{ center: boolean, zoom: number}>()
  const theMap = useRef(null)

  // Map initialization
  useEffect(() => {
    if (theMap.current) return // initialised already?

    theMap.current = L.map(id, {
      boxZoom: false,
      keyboard: false,
      zoomSnap: 1,
      maxZoom: 18,
      minZoom: 5,
      worldCopyJump: true
    })

    if (history.location.state?.center) {
      theMap.current.setView(history.location.state.center, history.location.state.zoom)
    } else {
      const spainBounds = L.latLngBounds(L.latLng(44, -18), L.latLng(27, 4))
      theMap.current.fitBounds(spainBounds)
    }

    new L.TileLayer(tileUrl, {
      maxZoom: 19,
      referrerPolicy: 'strict-origin-when-cross-origin',
    }).addTo(theMap.current)

    // Add a top-center target for messages
    theMap.current._controlCorners.topcenter =
      L.DomUtil.create('div', 'leaflet-top leaflet-center', theMap.current._controlContainer)

    if (window.ResizeObserver) {
      const observer = new window.ResizeObserver(() => {
        theMap.current.invalidateSize()
      })
      observer.observe(document.getElementById(id))
    }

  }, [id, history.location.state])

  return theMap
}

const assetIcon = L && L.divIcon({
  className: 'map-marker-asset',
  iconSize: null
})

const selfIcon = L && L.divIcon({
  className: 'map-marker-self',
  iconSize: null
})

export const useMapMarkers = (theMap: React.MutableRefObject<any>, assets: Asset[], shouldCenter?: boolean) => {
  const markers = useRef(null)
  const history = useHistory()

  useEffect(() => {
    if (markers.current) {
      theMap.current.removeLayer(markers.current)
    }

    markers.current = L.markerClusterGroup({
      animate: false,
      chunkedLoading: true,
      showCoverageOnHover: false
    })

    assets.forEach(asset => {
      L.marker([parseFloat(asset?.lat), parseFloat(asset?.lon)], { icon: assetIcon })
        .addTo(markers.current)
        .on('click', () => history.push('/assets/' + asset.slug, { useBack: true }))
    })

    if (shouldCenter) {
      if (assets.length === 1) {
        theMap.current.setView([parseFloat(assets[0].lat), parseFloat(assets[0].lon)], 14)
      } else if (assets.length > 1) {
        // theMap.current.fitBounds(markers) // ?
      }
    }

    markers.current.addTo(theMap.current)
  }, [assets, history, shouldCenter, theMap])
}

export const useMapSelf = (theMap: React.MutableRefObject<any>, shouldCenter?: boolean) => {
  const history = useHistory<{ center: boolean }>()
  const loc = useGeolocation(true)
  const marker = useRef(null)
  const circle = useRef(null)
  const [isCentered, setCentered] = useState(true)
  const inhibit = useRef(false)
  const warning = useRef(null)
  const noGeo = !loc

  useEffect(() => {
    if (history.location.state?.center) setCentered(false)
  }, [history.location.state?.center])

  useEffect(() => {
    if (marker.current) {
      theMap.current.removeLayer(marker.current)
      theMap.current.removeLayer(circle.current)
    }
    if (loc?.length) {
      const latlng = [loc[0], loc[1]]
      marker.current = L.marker(latlng, { icon: selfIcon })
      marker.current.addTo(theMap.current)
      circle.current = L.circle(latlng, { fill: false, radius: 300, weight: 1 })
      circle.current.addTo(theMap.current)

      if (shouldCenter && isCentered) {
        inhibit.current = true
        theMap.current.setView(loc, 14)
        inhibit.current = false
      }
    }
  }, [loc, isCentered, shouldCenter, theMap])

  useEffect(() => {
    if (noGeo) {
      const Textbox = L.Control.extend({
        onAdd: function () {
          const text = L.DomUtil.create('div')
          text.id = 'geo-error'
          text.innerHTML = 'Ubicación no disponible'
          return text
        },
        onRemove: function () {}
      })
      warning.current = new Textbox({ position: 'topcenter' })
      warning.current.addTo(theMap.current)
    } else {
      warning.current && theMap.current.removeControl(warning.current)
    }
  }, [noGeo, theMap])

  useEffect(() => {
    if (shouldCenter && isCentered) {
      const handler = () => {
        if (!inhibit.current) {
          setCentered(false)
        }
      }
      const copyCurrent = theMap.current
      copyCurrent.on('movestart', handler)
      return () => copyCurrent.off('movestart', handler)
    }
  }, [shouldCenter, isCentered, theMap])

  return () => {
    setCentered(true)
  }
}

export const useMapLastPosition = (theMap: React.MutableRefObject<any>) => {
  const history = useHistory()
  useEffect(() => {
    const handler = () => {
      const { lat, lng } = theMap.current.getCenter()
      const zoom = theMap.current.getZoom()
      history.replace(history.location.pathname, {
        center: { lat, lng },
        zoom: zoom
      })
    }
    const copyCurrent = theMap.current
    copyCurrent.on('moveend', handler)
    return () => copyCurrent.off('moveend', handler)
  }, [history, theMap])
}
